'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { Coffee } from 'lucide-react'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/**
 * Hero del tema "cafeto" (café/panadería): acogedor y artesanal. Nombre en
 * Fraunces dentro de un marco dashed tipo papel kraft, sticker circular de
 * acento ligeramente rotado (sello de la casa), y la foto enmarcada en un
 * "sobre" con borde dashed — nada va a sangre. La barra superior usa modo
 * sólido (navSolid). Lee tokens del tema (--ms-*).
 */
export function HeroCafeto({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)
  const hasMedia = !!(heroImageUrl || heroVideoUrl)

  return (
    <section className="w-full" style={{ background: 'var(--ms-bg)' }}>
      {/* pt despeja la barra superior fija (navSolid) */}
      <div className="mx-auto max-w-4xl px-6 sm:px-8 pt-24 sm:pt-28 pb-10 text-center">
        {/* Sticker de la casa: círculo de acento rotado con grano de café */}
        <span
          className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-6 -rotate-6"
          style={{
            background: 'var(--ms-accent)',
            boxShadow: '0 10px 22px -10px color-mix(in srgb, var(--ms-accent) 65%, transparent)',
          }}
          aria-hidden
        >
          <Coffee className="w-6 h-6 text-white" />
        </span>

        {city && (
          <div
            className="text-[11px] font-bold uppercase tracking-[0.26em] mb-5"
            style={{ color: 'var(--ms-text-muted)' }}
          >
            {city}
          </div>
        )}

        {/* Nombre en marco dashed tipo kraft */}
        <div
          className="inline-block px-7 sm:px-10 py-4 sm:py-5"
          style={{
            border: '1.5px dashed color-mix(in srgb, var(--ms-accent) 45%, var(--ms-border))',
            borderRadius: 'calc(var(--ms-radius) * 1.2)',
          }}
        >
          <h1
            className="font-semibold leading-[1.04] tracking-[-0.01em] text-[clamp(36px,6.5vw,62px)]"
            style={{ fontFamily: 'var(--ms-font-display)', color: 'var(--ms-text)' }}
          >
            {businessName}
          </h1>
        </div>

        {tagline && (
          <p className="mt-5 max-w-xl mx-auto text-[16px] sm:text-[17px] leading-relaxed" style={{ color: 'var(--ms-text-muted)' }}>
            {tagline}
          </p>
        )}

        {/* Meta discreta */}
        <div
          className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[13px]"
          style={{ color: 'var(--ms-text-muted)' }}
        >
          {statusLabel && (
            <span className="inline-flex items-center gap-1.5">
              <span
                className="w-[6px] h-[6px] rounded-full"
                style={{
                  background:
                    statusLabel.tone === 'open'
                      ? '#2fa96d'
                      : statusLabel.tone === 'soon'
                        ? '#f59e0b'
                        : 'var(--ms-text-muted)',
                }}
              />
              {statusLabel.text}
            </span>
          )}
          {statusLabel && <span aria-hidden>·</span>}
          <span>{t('pickupAvailable')}</span>
          {pickupWaitMinutes ? (
            <>
              <span aria-hidden>·</span>
              <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span>
            </>
          ) : null}
        </div>

        {/* CTAs */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onViewMenu}
            className="inline-flex items-center rounded-full px-8 py-3.5 text-[15px] font-bold transition hover:brightness-105 active:scale-[0.98]"
            style={{
              background: 'var(--ms-accent)',
              color: '#ffffff',
              boxShadow: '0 12px 26px -12px color-mix(in srgb, var(--ms-accent) 70%, transparent)',
            }}
          >
            {t('orderOnline')}
          </button>
          {hasDelivery && onDelivery && (
            <button
              type="button"
              onClick={onDelivery}
              className="inline-flex items-center rounded-full px-6 py-3.5 text-[14.5px] font-semibold transition hover:bg-black/5"
              style={{ border: '1.5px solid var(--ms-text)', color: 'var(--ms-text)' }}
            >
              {t('delivery')}
            </button>
          )}
        </div>
      </div>

      {/* Foto en "sobre" kraft: marco dashed con padding, esquinas muy redondas */}
      {hasMedia && (
        <div className="mx-auto max-w-5xl px-6 sm:px-8 pb-16">
          <div
            className="p-2 sm:p-2.5"
            style={{
              border: '1.5px dashed color-mix(in srgb, var(--ms-accent) 35%, var(--ms-border))',
              borderRadius: 'calc(var(--ms-radius) * 1.5)',
            }}
          >
            <div
              className="relative w-full overflow-hidden"
              style={{
                aspectRatio: '16 / 7',
                borderRadius: 'calc(var(--ms-radius) * 1.2)',
                boxShadow: 'var(--ms-card-shadow)',
              }}
            >
              {heroImageUrl ? (
                <Image src={heroImageUrl} alt={businessName} fill priority sizes="100vw" className="object-cover" />
              ) : heroVideoUrl ? (
                <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" src={heroVideoUrl} />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
